import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import type { Shop, ShopOutcome } from '../services/apiService';

// The three reads every Feeds surface needs, in one place.
//
// `/feeds`, `/feeds/add` and `/feeds/:ref` all answer questions about the same
// shop list, and each performs its own read: a room opened from the Dashboard's
// health banner has no hub state to inherit, so "load once and pass it down" is
// not available. The cost — hub, room, hub is three `getShops` — is recorded in
// the change's risks rather than hidden. What IS shared is the code, so the
// three cannot drift into three slightly different loading behaviours.
//
// TWO OF THE THREE READS ARE ALLOWED TO FAIL. The shop list is the screen's job;
// outcome is decision support and the stale-upload ids are a grouping input.
// Losing either must not cost the publisher the first.

export interface ShopOfferState {
  shops: Shop[] | null;
  consent: string;
  failed: boolean;
  outcome: ShopOutcome | null;
  /** Feed ids the SERVER calls stale. `STALE_UPLOAD_DAYS` is never restated here. */
  staleFeedIds: ReadonlySet<number>;
  reload: () => Promise<void>;
}

export function useShopOffer(): ShopOfferState {
  const [shops, setShops] = useState<Shop[] | null>(null);
  const [consent, setConsent] = useState('');
  const [failed, setFailed] = useState(false);
  const [outcome, setOutcome] = useState<ShopOutcome | null>(null);
  const [staleFeedIds, setStaleFeedIds] = useState<ReadonlySet<number>>(new Set());

  const reload = useCallback(
    () => apiService.getShops()
      .then(o => { setShops(o.shops); setConsent(o.consent); setFailed(false); })
      .catch(() => setFailed(true)),
    []
  );

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    apiService.getShopOutcome().then(setOutcome).catch(() => setOutcome(null));
    apiService.getSetupStatus()
      .then(s => setStaleFeedIds(new Set(((s as any).staleUploads ?? []).map((f: any) => f.id))))
      .catch(() => setStaleFeedIds(new Set()));
  }, []);

  return { shops, consent, failed, outcome, staleFeedIds, reload };
}

/** The outcome row for a shop, or null — an absence, never a zero. */
export function outcomeRowFor(outcome: ShopOutcome | null, shop: Shop) {
  // Keyed on feedId: a shop with no feed of its own has nothing to report, and
  // "0 shown · 0 clicked" there would read as a verdict rather than an absence.
  return shop.feedId === null || !outcome
    ? null
    : outcome.rows.find(r => r.feedId === shop.feedId) ?? null;
}
